import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  ScatterController,
  TimeScale,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-moment";
import annotationPlugin from "chartjs-plugin-annotation";
import { Bar, Chart } from "react-chartjs-2";
import { useAuth0 } from "@auth0/auth0-react";
import useTradeAnalysis, { TradePerformanceRow } from "./hooks/useTradeAnalysis.ts";
import useIssueCusips from "./hooks/useIssueCusips.ts";
import { formatCusip } from "../tools/cusips.ts";
import SplineApi from "../tools/spline-api.js";
import moment from "moment";

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, PointElement, LineElement, ScatterController, TimeScale, Tooltip, Legend, annotationPlugin);

function buildDatetime(row: TradePerformanceRow): number {
  return moment(`${row.trade_date} ${row.time_of_trade}`, "YYYY-MM-DD HH:mm:ss").valueOf();
}

function AccumulatedVolumeChart({ rows, title, wholeDeal = false }: { rows: TradePerformanceRow[]; title: string; wholeDeal?: boolean }) {
  const { offeringTs, showOfferingLabel } = useMemo(() => {
    const date = rows.find((r) => r.offering_date != null)?.offering_date ?? null;
    const ts = date ? moment.utc(date, "YYYY-MM-DD").valueOf() : null;
    const hasData = date != null && rows.some((r) => r.trade_date === date);
    return { offeringTs: ts, showOfferingLabel: !hasData };
  }, [rows]);

  const chartData = useMemo(() => {
    // For whole deal, compute total par issued across all unique cusips
    let totalDealSize = 0;
    if (wholeDeal) {
      const seen = new Set<string>();
      for (const r of rows) {
        if (!seen.has(r.cusip) && r.total_maturity_offering_amt != null) {
          seen.add(r.cusip);
          totalDealSize += r.total_maturity_offering_amt;
        }
      }
    }

    // Group volume by day, split into List Price and non-List Price
    const byDay = new Map<string, { lp: number; nonLp: number }>();
    for (const r of rows) {
      const day = r.trade_date;
      if (!byDay.has(day)) byDay.set(day, { lp: 0, nonLp: 0 });
      const entry = byDay.get(day)!;
      const vol = wholeDeal && totalDealSize > 0 ? r.par_traded / totalDealSize : (r.volume ?? 0);
      if (r.is_lp) entry.lp += vol;
      else entry.nonLp += vol;
    }
    const days = [...byDay.keys()].sort();
    const timestamps = days.map((d) => moment(d, "YYYY-MM-DD").valueOf());

    const lpDataset = {
      label: "List Price Volume",
      data: days.map((d, i) => ({ x: timestamps[i], y: byDay.get(d)!.lp * 100 })),
      backgroundColor: "rgba(54, 162, 235, 0.8)",
      borderColor: "rgb(54, 162, 235)",
      borderWidth: 1,
      barThickness: 10,
    };

    const nonLpDataset = {
      label: "Non-List Price Volume",
      data: days.map((d, i) => ({ x: timestamps[i], y: byDay.get(d)!.nonLp * 100 })),
      backgroundColor: "rgba(255, 99, 132, 0.8)",
      borderColor: "rgb(255, 99, 132)",
      borderWidth: 1,
      barThickness: 10,
    };

    return { datasets: [lpDataset, nonLpDataset] };
  }, [rows, wholeDeal]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    scales: {
      x: {
        type: "time" as const,
        time: { unit: "day" as const, displayFormats: { day: "MM/DD/YY" } },
        title: { display: true, text: "Trade Date" },
      },
      y: {
        title: { display: true, text: "Volume (%)" },
        ticks: { callback: (v: any) => `${v.toFixed(1)}%` },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: (items: any[]) => moment(items[0].parsed.x).format("MM/DD/YYYY"),
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(2)}%`,
        },
      },
      annotation: offeringTs != null ? {
        annotations: {
          offeringLine: {
            type: "line" as const,
            xMin: offeringTs,
            xMax: offeringTs,
            borderColor: "rgba(76, 175, 80, 0.9)",
            borderWidth: 2,
            borderDash: [6, 4],
            label: {
              content: "Offering Date",
              display: showOfferingLabel,
              position: "end" as const,
              rotation: -90,
              xAdjust: 10,
              font: { size: 11 },
              color: "rgba(76, 175, 80, 1)",
              backgroundColor: "transparent",
            },
          },
        },
      } : {},
    },
  }), [offeringTs, showOfferingLabel]);

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" mb={1}>Volume by Day — {title}</Typography>
      <Box sx={{ height: 250 }}>
        <Bar data={chartData} options={options as any} />
      </Box>
    </Paper>
  );
}

type YieldMode = "yield" | "spread";

function YieldEvolutionChart({ rows, wholeDeal = false, title, predictedYield = null, predictedSpread = null }: { rows: TradePerformanceRow[]; wholeDeal?: boolean; title: string; predictedYield?: number | null; predictedSpread?: number | null }) {
  const [mode, setMode] = useState<YieldMode>("spread");

  const offeringTs = useMemo(() => {
    const date = rows.find((r) => r.offering_date != null)?.offering_date ?? null;
    return date ? moment.utc(date, "YYYY-MM-DD").valueOf() : null;
  }, [rows]);

  const offeringYield = useMemo(() =>
    wholeDeal ? null : (rows.find((r) => r.offering_yield != null)?.offering_yield ?? null),
  [rows, wholeDeal]);

  const offeringSpread = useMemo(() =>
    wholeDeal ? null : (rows.find((r) => r.offering_spread != null)?.offering_spread ?? null),
  [rows, wholeDeal]);

  const chartData = useMemo(() => {
    if (wholeDeal) {
      // Build per-cusip state: offering size and starting yield/spread
      const cusipState = new Map<string, { size: number; current: number | null }>();
      for (const r of rows) {
        if (!cusipState.has(r.cusip)) {
          cusipState.set(r.cusip, {
            size: r.total_maturity_offering_amt ?? 0,
            current: mode === "yield" ? r.offering_yield : null,
          });
        }
      }

      const trades = [...rows]
        .filter((r) => (mode === "yield" ? r.traded_yield != null : r.traded_spread != null))
        .sort((a, b) => buildDatetime(a) - buildDatetime(b));

      const nonMarkupPoints: { x: number; y: number; is_lp: boolean; cusip: string }[] = [];
      const markupPoints: { x: number; y: number; cusip: string }[] = [];

      for (const trade of trades) {
        const state = cusipState.get(trade.cusip);
        if (!state) continue;

        state.current = mode === "yield" ? trade.traded_yield : trade.traded_spread;

        let totalSize = 0;
        let weightedSum = 0;
        for (const s of cusipState.values()) {
          if (s.current == null || s.size === 0) continue;
          totalSize += s.size;
          weightedSum += s.current * s.size;
        }

        if (totalSize === 0) continue;
        const wavg = weightedSum / totalSize;

        if (trade.is_markup) {
          markupPoints.push({ x: buildDatetime(trade), y: wavg, cusip: trade.cusip });
        } else {
          nonMarkupPoints.push({ x: buildDatetime(trade), y: wavg, is_lp: trade.is_lp, cusip: trade.cusip });
        }
      }

      return {
        datasets: [
          {
            type: "line" as const,
            label: mode === "yield" ? "Yield" : "Spread",
            data: nonMarkupPoints.map((p) => ({ x: p.x, y: p.y, cusip: p.cusip })),
            borderColor: "rgb(255, 99, 132)",
            backgroundColor: nonMarkupPoints.map((p) => p.is_lp ? "rgba(255,99,132,0.8)" : "rgba(255,99,132,0.3)"),
            pointStyle: nonMarkupPoints.map((p) => p.is_lp ? "circle" : "rectRot"),
            pointRadius: 5,
            showLine: true,
            tension: 0,
          },
          {
            type: "scatter" as const,
            label: "Markup",
            data: markupPoints,
            backgroundColor: "rgba(255, 165, 0, 0.85)",
            borderColor: "rgb(255, 140, 0)",
            pointStyle: "crossRot",
            pointRadius: 7,
            pointBorderWidth: 2,
          },
        ],
      };
    }

    const getValue = (r: TradePerformanceRow) => mode === "yield" ? r.traded_yield : r.traded_spread;
    const sorted = [...rows]
      .filter((r) => getValue(r) != null)
      .sort((a, b) => buildDatetime(a) - buildDatetime(b));

    const nonMarkup = sorted.filter((r) => !r.is_markup);
    const markup = sorted.filter((r) => r.is_markup);

    const datasets: any[] = [
      {
        type: "line" as const,
        label: mode === "yield" ? "Yield" : "Spread",
        data: nonMarkup.map((r) => ({ x: buildDatetime(r), y: getValue(r) })),
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: nonMarkup.map((r) => r.is_lp ? "rgba(255,99,132,0.8)" : "rgba(255,99,132,0.3)"),
        pointStyle: nonMarkup.map((r) => r.is_lp ? "circle" : "rectRot"),
        pointRadius: 5,
        showLine: true,
        tension: 0,
      },
      {
        type: "scatter" as const,
        label: "Markup",
        data: markup.map((r) => ({ x: buildDatetime(r), y: getValue(r) })),
        backgroundColor: "rgba(255, 165, 0, 0.85)",
        borderColor: "rgb(255, 140, 0)",
        pointStyle: "crossRot",
        pointRadius: 7,
        pointBorderWidth: 2,
      },
    ];

    return { datasets };
  }, [rows, mode, wholeDeal]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    scales: {
      x: {
        type: "time" as const,
        time: { unit: "day" as const, displayFormats: { day: "MM/DD/YY", hour: "MM/DD HH:mm" } },
        title: { display: true, text: "Trade Date/Time" },
      },
      y: {
        title: { display: true, text: mode === "yield" ? "Yield" : "Spread" },
        ticks: { callback: (v: any) => `${v.toFixed(3)}` },
      },
    },
    plugins: {
      tooltip: {
        mode: "nearest" as const,
        intersect: true,
        callbacks: {
          title: (items: any[]) => moment(items[0].parsed.x).format("MM/DD/YYYY HH:mm"),
          label: (ctx: any) => {
            const label = mode === "yield" ? "Yield" : "Spread";
            if (wholeDeal) {
              const cusip = ctx.raw?.cusip ?? "";
              return `${cusip} — ${label}: ${ctx.parsed.y?.toFixed(3)}`;
            }
            if (ctx.dataset.label === "Markup") {
              return `Markup: ${ctx.parsed.y?.toFixed(3)}`;
            }
            const lpLabel = ctx.dataset.backgroundColor?.[ctx.dataIndex]?.includes("0.8") ? " (List Price)" : "";
            return `${label}: ${ctx.parsed.y?.toFixed(3)}${lpLabel}`;
          },
        },
      },
      legend: { display: false },
      annotation: {
        annotations: {
          ...(offeringTs != null ? {
            offeringLine: {
              type: "line" as const,
              xMin: offeringTs,
              xMax: offeringTs,
              borderColor: "rgba(76, 175, 80, 0.9)",
              borderWidth: 2,
              borderDash: [6, 4],
            },
          } : {}),
          ...(mode === "yield" && predictedYield != null ? {
            predictedYieldLine: {
              type: "line" as const,
              yMin: predictedYield,
              yMax: predictedYield,
              borderColor: "rgb(54, 162, 235)",
              borderWidth: 2,
              borderDash: [6, 4],
              label: {
                content: "Predicted Yield",
                display: true,
                position: "start" as const,
                xAdjust: 5,
                yAdjust: -10,
                font: { size: 11 },
                color: "rgb(54, 162, 235)",
                backgroundColor: "transparent",
              },
            },
          } : {}),
          ...(mode === "yield" && offeringYield != null ? {
            offeringYieldLine: {
              type: "line" as const,
              yMin: offeringYield,
              yMax: offeringYield,
              borderColor: "rgb(255, 152, 0)",
              borderWidth: 2,
              borderDash: [6, 4],
              label: {
                content: "Offering Yield",
                display: true,
                position: "start" as const,
                xAdjust: 5,
                yAdjust: -10,
                font: { size: 11 },
                color: "rgb(255, 152, 0)",
                backgroundColor: "transparent",
              },
            },
          } : {}),
          ...(mode === "spread" && predictedSpread != null ? {
            predictedSpreadLine: {
              type: "line" as const,
              yMin: predictedSpread,
              yMax: predictedSpread,
              borderColor: "rgb(54, 162, 235)",
              borderWidth: 2,
              borderDash: [6, 4],
              label: {
                content: "Predicted Spread",
                display: true,
                position: "start" as const,
                xAdjust: 5,
                yAdjust: -10,
                font: { size: 11 },
                color: "rgb(54, 162, 235)",
                backgroundColor: "transparent",
              },
            },
          } : {}),
          ...(mode === "spread" && offeringSpread != null ? {
            offeringSpreadLine: {
              type: "line" as const,
              yMin: offeringSpread,
              yMax: offeringSpread,
              borderColor: "rgb(255, 152, 0)",
              borderWidth: 2,
              borderDash: [6, 4],
              label: {
                content: "Offering Spread",
                display: true,
                position: "start" as const,
                xAdjust: 5,
                yAdjust: -10,
                font: { size: 11 },
                color: "rgb(255, 152, 0)",
                backgroundColor: "transparent",
              },
            },
          } : {}),
        },
      },
    },
  }), [mode, wholeDeal, offeringTs, offeringYield, offeringSpread, predictedYield, predictedSpread]);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" mb={1}>
        {mode === "yield" ? "Yield" : "Spread"} Evolution Over Time — {title}
        <Typography component="span" variant="caption" color="text.secondary" ml={1}>
            ● List Price &nbsp; ◆ Non-List Price &nbsp; ✕ Markup
          </Typography>
      </Typography>
      <Box sx={{ height: 250 }}>
        <Chart type="scatter" data={chartData as any} options={options as any} />
      </Box>
      <Box sx={{ mt: 1 }}>
        <FormControlLabel
          control={<Switch checked={mode === "yield"} onChange={(e) => setMode(e.target.checked ? "yield" : "spread")} size="small" />}
          label="Show yield"
        />
      </Box>
    </Paper>
  );
}

function CusipRow({
  cusip,
  summary,
  trades,
  selected,
  onSelect,
}: {
  cusip: string;
  summary: Pick<TradePerformanceRow, "maturity_date" | "total_maturity_offering_amt" | "offering_yield"> | undefined;
  trades: TradePerformanceRow[];
  selected: boolean;
  onSelect: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: "pointer", bgcolor: selected ? "action.hover" : "background.paper" }}
        onClick={onSelect}
      >
        <TableCell sx={{ py: 1.5, pr: 0, width: 32 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ py: 1.5 }}>{cusip}</TableCell>
        <TableCell sx={{ py: 1.5 }}>{summary?.maturity_date ? moment.utc(summary.maturity_date).format("MM/DD/YY") : "—"}</TableCell>
        <TableCell align="right" sx={{ py: 1.5 }}>
          {summary?.total_maturity_offering_amt != null
            ? (summary.total_maturity_offering_amt / 1_000_000).toFixed(1)
            : "—"}
        </TableCell>
        <TableCell align="right" sx={{ py: 1.5 }}>
          {summary?.offering_yield != null ? summary.offering_yield.toFixed(3) : "—"}
        </TableCell>
        <TableCell align="right" sx={{ py: 1.5 }}>
          {trades.filter((r) => r.is_lp).reduce((sum, r) => sum + (r.volume ?? 0), 0) > 0
            ? `${(trades.filter((r) => r.is_lp).reduce((sum, r) => sum + (r.volume ?? 0), 0) * 100).toFixed(1)}%`
            : "—"}
        </TableCell>
        <TableCell sx={{ py: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={(e) => { e.stopPropagation(); onSelect(); }}
            sx={{ minWidth: 60, color: "white" }}
          >
            Select
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, px: 0, border: open ? undefined : "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 1 }}>Date</TableCell>
                    <TableCell sx={{ py: 1 }}>Time</TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>Par ($K)</TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>Yield</TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>Spread</TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>Vol %</TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>Acc %</TableCell>
                    <TableCell sx={{ py: 1 }}>Type</TableCell>
                    <TableCell sx={{ py: 1 }}>List Price</TableCell>
                    <TableCell sx={{ py: 1 }}>Markup</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...trades].sort((a, b) => {
                    const dt = buildDatetime(a) - buildDatetime(b);
                    if (dt !== 0) return dt;
                    return (a.acc_volume ?? 0) - (b.acc_volume ?? 0);
                  }).map((t, i, arr) => {
                    const last = i === arr.length - 1;
                    const cellSx = (extra?: object) => ({ py: 1, ...(last && { borderBottom: "none" }), ...extra });
                    return (
                    <TableRow key={i}>
                      <TableCell sx={cellSx()}>{t.trade_date}</TableCell>
                      <TableCell sx={cellSx()}>{t.time_of_trade}</TableCell>
                      <TableCell align="right" sx={cellSx()}>{(t.par_traded / 1_000).toFixed(0)}</TableCell>
                      <TableCell align="right" sx={cellSx()}>{t.traded_yield?.toFixed(3) ?? "—"}</TableCell>
                      <TableCell align="right" sx={cellSx()}>{t.traded_spread != null ? (parseFloat(t.traded_spread.toFixed(3)) === 0 ? "0.000" : t.traded_spread.toFixed(3)) : "—"}</TableCell>
                      <TableCell align="right" sx={cellSx()}>{t.volume != null ? (t.volume * 100).toFixed(2) : "—"}</TableCell>
                      <TableCell align="right" sx={cellSx()}>{t.acc_volume != null ? (t.acc_volume * 100).toFixed(2) : "—"}</TableCell>
                      <TableCell sx={cellSx()}>{t.trade_type_indicator}</TableCell>
                      <TableCell sx={cellSx()}>{t.is_lp ? "Y" : ""}</TableCell>
                      <TableCell sx={cellSx()}>{t.is_markup ? "Y" : ""}</TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function TradeAnalysisMain() {
  const { getAccessTokenSilently } = useAuth0();
  const { fetchTradePerformance, rows, loading, error } = useTradeAnalysis();
  const { fetchIssueCusips, issueCusips } = useIssueCusips();
  const [cusipInput, setCusipInput] = useState("");
  const [selectedCusip, setSelectedCusip] = useState<string | null>(null);
  const [predictedYield, setPredictedYield] = useState<number | null>(null);
  const [predictedSpread, setPredictedSpread] = useState<number | null>(null);

  const canSearch = cusipInput.trim().length === 8 || cusipInput.trim().length === 9;

  const handleSearch = () => {
    const formatted = formatCusip(cusipInput);
    setSelectedCusip(null);
    fetchTradePerformance(formatted);
    fetchIssueCusips(formatted);
  };

  // Unique cusips excluding the deal-level row (where cusip == issue_id as string)
  const issueId = rows.length > 0 ? String(rows[0].issue_id) : null;

  // Auto-select whole deal when data loads
  useEffect(() => {
    if (issueId) setSelectedCusip(issueId);
  }, [issueId]);

  // Fetch predicted yield for single-CUSIP selection
  useEffect(() => {
    if (!selectedCusip || selectedCusip === issueId) {
      setPredictedYield(null);
      setPredictedSpread(null);
      return;
    }
    let cancelled = false;
    getAccessTokenSilently()
      .then((token) => new SplineApi(token).getPrimaryErrors({ cusip: selectedCusip }))
      .then((data: any[]) => {
        if (!cancelled) {
          setPredictedYield(data?.[0]?.predicted_yield ?? null);
          setPredictedSpread(data?.[0]?.predicted_spread ?? null);
        }
      })
      .catch(() => { if (!cancelled) { setPredictedYield(null); setPredictedSpread(null); } });
    return () => { cancelled = true; };
  }, [selectedCusip, issueId, getAccessTokenSilently]);
  // Union the cusips with trade data with the full issue cusip list, so bonds
  // with no trades yet still show up in the table (with blank trade columns).
  const cusipList = useMemo(() => {
    const seen = new Set<string>();
    return [...rows.map((r) => r.cusip), ...issueCusips.map((c) => c.cusip)]
      .filter((c) => {
        if (c === issueId || seen.has(c)) return false;
        seen.add(c);
        return true;
      })
      .sort();
  }, [rows, issueCusips, issueId]);

  const selectedRows = useMemo(() => {
    if (!selectedCusip) return [];
    return rows.filter((r) => r.cusip === selectedCusip);
  }, [rows, selectedCusip]);

  // For the whole deal chart, use all individual bond rows (not deal-level aggregate rows)
  const allBondRows = useMemo(() => rows.filter((r) => r.cusip !== issueId), [rows, issueId]);

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Deal Performance</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            label="CUSIP"
            size="small"
            value={cusipInput}
            onChange={(e) => setCusipInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && canSearch) handleSearch(); }}
            sx={{ width: 250 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={!canSearch || loading}
            sx={{ color: "white" }}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && rows.length > 0 && (
        <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
          {/* Left: cusip selector */}
          <Box sx={{ flexShrink: 0 }}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 1.5, width: 32 }} />
                    <TableCell sx={{ py: 1.5 }}>CUSIP</TableCell>
                    <TableCell sx={{ py: 1.5 }}>Maturity</TableCell>
                    <TableCell align="right" sx={{ py: 1.5 }}>Par Amount ($M)</TableCell>
                    <TableCell align="right" sx={{ py: 1.5 }}>Off. Yield</TableCell>
                    <TableCell align="right" sx={{ py: 1.5 }}>% Sold at List</TableCell>
                    <TableCell sx={{ py: 1.5 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {issueId && (
                    <TableRow
                      hover
                      sx={{ cursor: "pointer", bgcolor: selectedCusip === issueId ? "action.hover" : "background.paper" }}
                      onClick={() => setSelectedCusip(issueId)}
                    >
                      <TableCell sx={{ py: 1.5 }} />
                      <TableCell colSpan={5} sx={{ py: 1.5 }}>
                        <Typography variant="body2" fontWeight="bold">Whole Deal</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => { e.stopPropagation(); setSelectedCusip(issueId); }}
                          sx={{ minWidth: 60, color: "white" }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                  {cusipList.map((c) => (
                    <CusipRow
                      key={c}
                      cusip={c}
                      summary={rows.find((r) => r.cusip === c) ?? issueCusips.find((ic) => ic.cusip === c)}
                      trades={rows.filter((r) => r.cusip === c)}
                      selected={selectedCusip === c}
                      onSelect={() => setSelectedCusip(c)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Right: charts */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {selectedCusip ? (
              <>
                <AccumulatedVolumeChart
                  rows={selectedCusip === issueId ? allBondRows : selectedRows}
                  title={selectedCusip === issueId ? "Whole Deal" : selectedCusip}
                  wholeDeal={selectedCusip === issueId}
                />
                {selectedCusip !== issueId && (
                  <YieldEvolutionChart
                    rows={selectedRows}
                    title={selectedCusip}
                    predictedYield={predictedYield}
                    predictedSpread={predictedSpread}
                  />
                )}
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", py: 8 }}>
                <Typography color="text.secondary">Select a CUSIP or Whole Deal to view charts.</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {!loading && !error && rows.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <Typography color="text.secondary">Search by CUSIP to view trade performance.</Typography>
        </Box>
      )}
    </Box>
  );
}
